import React from 'react';
import {
  AnyAssetApplication,
  AssetParams,
  CollectorSet,
  createUrl,
  Flex,
  FormikType,
  Grid,
  IntervalSetting,
  NetworkAsset,
  PageContent,
  useAssetConfig,
  useAssetParams,
  useAssetTranslation,
} from '@mss/mss-component-library';
import { Spin } from 'antd';
import CollectorSegment from 'jsx/asset/components/CollectorSegment_formik_import';
import { useCollectorsQuery } from '../../../asset/services/queries';
import { useFormikContext } from 'formik';

type Props = { index?: number; overrideAssetParams?: Partial<AssetParams> };
export default function AssetCollectors<AssetType extends AnyAssetApplication>({
  index = 0,
  overrideAssetParams,
}: Props) {
  const { assetSubType } = useAssetParams(overrideAssetParams);
  const { t } = useAssetTranslation();
  const { matchingConfig } = useAssetConfig<AssetType>(assetSubType);
  const { values } = useFormikContext<FormikType<AssetType & NetworkAsset>>();
  const seriesValue = values.assets[index].values.series;
  const query = useCollectorsQuery({
    presetUrl: createUrl(
      useAssetParams(overrideAssetParams),
      `collectors${seriesValue ? `/?series_id=${seriesValue}` : '/'}`,
      true
    ),
    disabled: !matchingConfig.type,
  });

  const data = {
    ...(query.raw.data ?? {}),
    ...(matchingConfig.form.collectors.staticCollectors ?? {}),
  };
  const sets = Object.keys(data);

  if (query.raw.isLoading || !query.raw.isSuccess)
    return (
      <Flex h="center" v="center">
        <Spin />
      </Flex>
    );

  return (
    <PageContent title={t('Asset Collectors')}>
      <Grid.Row>
        {sets.map((set: string) => {
          const enrichedWithTooltip = {
            ...data[set],
            collectors: data[set].collectors.map(x => ({
              ...x,
              intervals: data[set].intervals ?? ({} as IntervalSetting),
              tooltip: matchingConfig.form.collectors.tooltips
                ? matchingConfig.form.collectors.tooltips[x.key]
                : null,
            })),
          } as CollectorSet;

          return (
            <Grid.Col width={4} key={set} className="mb-6">
              <CollectorSegment
                overrideAssetParams={overrideAssetParams}
                collectors={enrichedWithTooltip}
                formIndex={index}
                set={
                  Object.keys(
                    matchingConfig.form.collectors.staticCollectors ?? {}
                  ).indexOf(set) > -1 ||
                  matchingConfig.form.collectors.disablePrefix
                    ? set
                    : `_collectortype_${set}`
                }
              />
            </Grid.Col>
          );
        })}
      </Grid.Row>
    </PageContent>
  );
}
